import { useEffect, useState } from "react";
import { Copy, Check, Link2, MousePointerClick, ShoppingCart, Hourglass, Wallet, BadgeDollarSign } from "lucide-react";
import api from "@/lib/api";

const STATUS_STYLES = {
  pending: "bg-[var(--paper-3)] text-[var(--ink-muted)]",
  earned: "bg-[var(--terra)]/15 text-[var(--terra)]",
  paid: "bg-[var(--olive)]/15 text-[var(--olive)]",
  reversed: "bg-[var(--crimson)]/15 text-[var(--crimson)]",
};

export default function Affiliate() {
  const [data, setData] = useState(null);
  const [notEnrolled, setNotEnrolled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState("");

  useEffect(() => {
    api.get("/affiliate/me")
      .then((r) => setData(r.data))
      .catch((e) => { if (e.response?.status === 404) setNotEnrolled(true); })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--ink-muted)]">Loading affiliate data…</div>;
  }
  if (notEnrolled) {
    return (
      <div className="border border-[var(--line)] rounded-md bg-[var(--paper-2)] p-8 text-center" data-testid="affiliate-not-enrolled">
        <h2 className="font-display text-2xl">Invite-only program.</h2>
        <p className="mt-2 text-sm text-[var(--ink-soft)] max-w-md mx-auto">
          The Nebula Aminos affiliate program is currently invite-only. Reach out to{" "}
          <a href="mailto:support@nebulaaminos.com" className="text-[var(--terra)] underline">support@nebulaaminos.com</a>{" "}
          if you&apos;d like to partner with us.
        </p>
      </div>
    );
  }
  if (!data) return null;

  const { affiliate, stats, commissions, payouts } = data;
  const shareLink = `${window.location.origin}/?ref=${affiliate.code}`;
  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(""), 1500);
    } catch { /* long-press to select */ }
  };

  return (
    <div className="space-y-8" data-testid="affiliate-dashboard">
      <div>
        <div className="eyebrow">Affiliate program</div>
        <h2 className="font-display text-3xl mt-1">Your partner dashboard.</h2>
        {affiliate.status !== "active" && (
          <div className="mt-3 inline-flex px-3 py-1 rounded-full bg-[var(--crimson)]/15 text-[var(--crimson)] font-mono text-[10.5px] uppercase tracking-[0.16em]" data-testid="affiliate-paused">
            Code paused — contact support
          </div>
        )}
      </div>

      {/* Code + share link */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border border-[var(--line)] rounded-md bg-[var(--paper-2)] p-5">
          <div className="eyebrow">Your code · {affiliate.discount_pct}% off for buyers</div>
          <div className="mt-2 flex items-center gap-2">
            <code className="font-display text-2xl font-semibold tracking-wide flex-1" data-testid="affiliate-code">{affiliate.code}</code>
            <button onClick={() => copy(affiliate.code, "code")} className="px-2.5 py-2 border border-[var(--line)] rounded-md hover:border-[var(--terra)]" data-testid="copy-affiliate-code" aria-label="Copy code">
              {copied === "code" ? <Check className="h-4 w-4 text-[var(--terra)]" /> : <Copy className="h-4 w-4" />}
            </button>
          </div>
          <div className="mt-2 font-mono text-[11px] text-[var(--ink-muted)]">You earn {affiliate.commission_pct}% of each order&apos;s discounted subtotal.</div>
        </div>
        <div className="border border-[var(--line)] rounded-md bg-[var(--paper-2)] p-5">
          <div className="eyebrow flex items-center gap-1.5"><Link2 className="h-3.5 w-3.5" /> Share link</div>
          <div className="mt-2 flex items-center gap-2">
            <code className="font-mono text-xs break-all flex-1" data-testid="affiliate-share-link">{shareLink}</code>
            <button onClick={() => copy(shareLink, "link")} className="px-2.5 py-2 border border-[var(--line)] rounded-md hover:border-[var(--terra)]" data-testid="copy-share-link" aria-label="Copy link">
              {copied === "link" ? <Check className="h-4 w-4 text-[var(--terra)]" /> : <Copy className="h-4 w-4" />}
            </button>
          </div>
          <div className="mt-2 font-mono text-[11px] text-[var(--ink-muted)]">Visitors landing on this link get your code pre-filled at checkout.</div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat icon={MousePointerClick} label="Link clicks" value={stats.clicks} testid="stat-clicks" />
        <Stat icon={ShoppingCart} label="Orders" value={stats.orders} testid="stat-orders" />
        <Stat icon={Hourglass} label="Pending" value={`$${stats.pending_usd.toFixed(2)}`} testid="stat-pending" />
        <Stat icon={Wallet} label="Earned balance" value={`$${stats.earned_usd.toFixed(2)}`} accent testid="stat-earned" />
        <Stat icon={BadgeDollarSign} label="Paid out" value={`$${stats.paid_usd.toFixed(2)}`} testid="stat-paid" />
      </div>

      {/* Commissions */}
      <section>
        <div className="eyebrow mb-3">Commission history</div>
        <div className="border border-[var(--line)] rounded-md bg-[var(--paper-2)] overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--line)] text-left font-mono text-[10.5px] uppercase tracking-[0.16em] text-[var(--ink-muted)]">
                <th className="px-4 py-2.5">Order</th>
                <th className="px-4 py-2.5">Date</th>
                <th className="px-4 py-2.5 text-right">Order subtotal</th>
                <th className="px-4 py-2.5 text-right">Commission</th>
                <th className="px-4 py-2.5">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--line)]">
              {commissions.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-6 text-center font-mono text-xs text-[var(--ink-muted)]">No referred orders yet — share your link to get started.</td></tr>
              )}
              {commissions.map((c) => (
                <tr key={c.id} data-testid={`commission-${c.order_number}`}>
                  <td className="px-4 py-2.5 font-mono text-xs">{c.order_number}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-[var(--ink-muted)]">{c.created_at?.slice(0, 10)}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-right">${Number(c.order_subtotal || 0).toFixed(2)}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-right font-semibold">${Number(c.commission_usd || 0).toFixed(2)}</td>
                  <td className="px-4 py-2.5">
                    <span className={`inline-flex px-2 py-0.5 rounded-full font-mono text-[10px] uppercase tracking-[0.14em] ${STATUS_STYLES[c.status] || ""}`}>{c.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Payouts */}
      <section>
        <div className="eyebrow mb-3">Payout history</div>
        <div className="border border-[var(--line)] rounded-md bg-[var(--paper-2)] overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--line)] text-left font-mono text-[10.5px] uppercase tracking-[0.16em] text-[var(--ink-muted)]">
                <th className="px-4 py-2.5">Date</th>
                <th className="px-4 py-2.5 text-right">Amount</th>
                <th className="px-4 py-2.5">Method</th>
                <th className="px-4 py-2.5">Reference</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--line)]">
              {payouts.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-6 text-center font-mono text-xs text-[var(--ink-muted)]">No payouts yet. Earned balance is paid out manually by the Nebula Aminos team.</td></tr>
              )}
              {payouts.map((p) => (
                <tr key={p.id} data-testid={`payout-${p.id}`}>
                  <td className="px-4 py-2.5 font-mono text-xs">{p.created_at?.slice(0, 10)}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-right font-semibold">${Number(p.amount_usd || 0).toFixed(2)}</td>
                  <td className="px-4 py-2.5 font-mono text-xs uppercase">{p.method}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-[var(--ink-muted)]">{p.reference || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, accent, testid }) => (
  <div className={`border rounded-md p-4 ${accent ? "border-[var(--terra)] bg-[var(--terra)]/10" : "border-[var(--line)] bg-[var(--paper-2)]"}`} data-testid={testid}>
    <Icon className={`h-4 w-4 ${accent ? "text-[var(--terra)]" : "text-[var(--ink-muted)]"}`} strokeWidth={1.6} />
    <div className="mt-2 font-display text-xl font-semibold">{value}</div>
    <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--ink-muted)] mt-0.5">{label}</div>
  </div>
);
